import { useState, useEffect, useCallback, useMemo } from 'react';

import { useCompanies, useRoles, useUsers } from '@adsk/alloy-provider';
import { useLocale } from '@adsk/alloy-react-locale';

import {
  MENTION_CONFIRMATION_MENTIONS_LIMIT,
  MENTION_CONFIRMATION_USERS_LIMIT,
  MENTION_TYPES,
} from '../consts';
import { parseMentionsText } from '../utils/parseMentionsText';

function useMentionConfirmation(text: string, onSubmit: () => void) {
  const { T } = useLocale(({ locale }) => require(`../i18n/${locale}.json`));
  const [confirmationModal, setConfirmationModal] = useState(false);
  const [numberOfMembers, setNumberOfMembers] = useState(0);

  const users = useUsers();
  const roles = useRoles();
  const companies = useCompanies();

  const getCompanyIdByMemberGroupId = useCallback(
    (memberGroupId: string): string | undefined => {
      return companies.data?.find(
        (company) => company.memberGroupId === memberGroupId,
      )?.id;
    },
    [companies],
  );

  const getRoleIdByMemberGroupId = useCallback(
    (memberGroupId: string): string | undefined => {
      return roles.data?.find((role) => role.memberGroupId === memberGroupId)
        ?.id;
    },
    [roles],
  );

  const mentions = useMemo(() => parseMentionsText(text), [text]);
  const rolesIdsFromMentions = useMemo(
    () =>
      mentions
        .filter((mention) => mention.type === MENTION_TYPES.ROLE)
        .map((mention) => {
          return getRoleIdByMemberGroupId(mention.id);
        }),
    [mentions, getRoleIdByMemberGroupId],
  );
  const companiesIdsFromMentions = useMemo(
    () =>
      mentions
        .filter((mention) => mention.type === MENTION_TYPES.COMPANY)
        .map((mention) => {
          return getCompanyIdByMemberGroupId(mention.id);
        }),
    [mentions, getCompanyIdByMemberGroupId],
  );
  const displayMentions = useMemo(
    () =>
      mentions
        .filter((mention) => mention.type !== MENTION_TYPES.USER)
        .map((mention) => `@${mention.name}`),
    [mentions],
  );

  const moreMentionsCount =
    displayMentions.length - MENTION_CONFIRMATION_MENTIONS_LIMIT;

  useEffect(() => {
    const usersWithRoles = users.data?.filter((user) =>
      rolesIdsFromMentions.find((id) => id && user.roleIds?.includes(id)),
    );
    const usersWithCompanies = users.data?.filter(
      (user) => companiesIdsFromMentions?.includes(user.companyId),
    );
    const totalUniqueUsers = new Set([
      ...usersWithRoles,
      ...usersWithCompanies,
    ]);

    setNumberOfMembers(totalUniqueUsers.size);
  }, [
    rolesIdsFromMentions,
    companiesIdsFromMentions,
    users.data,
    users.loading,
  ]);

  const onSubmitCallback = useCallback(() => {
    if (numberOfMembers > MENTION_CONFIRMATION_USERS_LIMIT) {
      setConfirmationModal(true);
    } else {
      onSubmit();
    }
  }, [numberOfMembers, onSubmit]);

  const onConfirmCallback = useCallback(() => {
    setConfirmationModal(false);
    onSubmit();
  }, [onSubmit]);

  const onCloseCallback = useCallback(() => {
    setConfirmationModal(false);
  }, []);

  return {
    onSubmit: onSubmitCallback,
    onConfirm: onConfirmCallback,
    onClose: onCloseCallback,
    isConfirmationButtonAlert: false,
    show: confirmationModal,
    cancelButtonLabel: T.translate('CONFIRMATION_BACK')?.toString(),
    confirmationButtonLabel: T.translate('CONFIRMATION_SEND')?.toString(),
    title: T.translate('CONFIRMATION_TITLE', {
      count: numberOfMembers,
    }),
    message: T.translate('CONFIRMATION_MESSAGE', {
      firstMention: displayMentions[0],
      secondMention: displayMentions[1],
      countOfRest: moreMentionsCount,
      context: displayMentions.length,
    }),
  };
}

export default useMentionConfirmation;
